import { z } from "zod";
import { anomalyAlertDetailSchema, ANOMALY_ALERT_KIND_BEHAVIOR } from "./cooling-freeze-anomaly.js";

/**
 * Contract US-E19.F7-0206-S02 (@UC9320) — DISPUTE → REMEDIATION + ADMIN SECURITY CONSOLE.
 * 🔴 @gate money + ledger + rbac.
 *
 * BA NHÓM DTO (đọc TÁCH BẠCH khỏi can-thiệp — bất biến ép ngay ở contract):
 *  (1) `disputeRemediationSchema` — khắc phục TIỀN của 1 case: `remediation` là **literal 'reverse'**
 *      (vòng này KHÔNG có kind nào khác ⇒ không thể lén "sửa số" mà không đi qua đường đảo).
 *      `idempotencyKey` BẮT BUỘC (bind dispute + actor ở service) ⇒ apply 2× = ĐÚNG 1 REVERSAL.
 *      `reason` BẮT BUỘC (dấu vết: admin KHÔNG được đổi tiền của nhóm mà không nói vì sao).
 *      🔴 KHÔNG có field số tiền — remediation KHÔNG BAO GIỜ "gõ số mới": nó ĐẢO bút toán sai (số tiền
 *      lấy từ chính bút toán gốc). Không có đường nào để admin nhập một con số vào sổ từ đây.
 *  (2) `securityConsoleReadSchema` — READ-ONLY tổng hợp: quỹ đóng băng · cảnh báo hành vi (F2) ·
 *      chi đang trong cửa sổ nguội · audit gần đây. **Mọi field tiền = string BigInt minor | null**
 *      (null = redact theo vai — `MONEY_VISIBLE_ROLES`). `z.number()` cho tiền là CẤM (B1 no-float).
 *  (3) `securityInterveneSchema` — CAN THIỆP (mutation) tách hẳn khỏi (2): enum 3 kind + `reason`
 *      bắt buộc. KHÔNG có field tiền ⇒ endpoint can-thiệp KHÔNG THỂ nhận một con số để ghi vào sổ.
 */

/* ───────────────────── (1) REMEDIATION dispute → REVERSAL ───────────────────── */

export const DISPUTE_REMEDIATION_KIND = {
  NONE: "none",
  REVERSE: "reverse",
} as const;
export type DisputeRemediationKind =
  (typeof DISPUTE_REMEDIATION_KIND)[keyof typeof DISPUTE_REMEDIATION_KIND];

/** Whitelist kind (mirror CHECK db `disputes_remediation_kind_check`). */
export const disputeRemediationKindSchema = z.enum([
  DISPUTE_REMEDIATION_KIND.NONE,
  DISPUTE_REMEDIATION_KIND.REVERSE,
]);

export const REMEDIATION_REASON_MIN_LENGTH = 8;
export const REMEDIATION_REASON_MAX_LENGTH = 2000;
const REMEDIATION_IDEMPOTENCY_KEY_MAX_LENGTH = 200;
const REMEDIATION_INCIDENT_REF_MAX_LENGTH = 200;

/**
 * Body khắc phục 1 case. `remediation` = literal 'reverse' (KHÔNG enum mở rộng — thêm kind mới phải
 * đi qua review contract). `.strict()` ⇒ field lạ (vd `amountMinor`) làm parse FAIL.
 */
export const disputeRemediationSchema = z
  .object({
    targetTxId: z.string().uuid(),
    remediation: z.literal(DISPUTE_REMEDIATION_KIND.REVERSE),
    idempotencyKey: z.string().min(1).max(REMEDIATION_IDEMPOTENCY_KEY_MAX_LENGTH),
    reason: z.string().min(REMEDIATION_REASON_MIN_LENGTH).max(REMEDIATION_REASON_MAX_LENGTH),
    sourceIncidentRef: z.string().min(1).max(REMEDIATION_INCIDENT_REF_MAX_LENGTH),
  })
  .strict();

export type DisputeRemediationBody = z.infer<typeof disputeRemediationSchema>;

/** Kết quả khắc phục — CON TRỎ (không tiền thô). `alreadyRemediated` = replay idempotent. */
export const disputeRemediationResponseSchema = z
  .object({
    disputeId: z.string().uuid(),
    status: z.literal("resolved"),
    remediationKind: z.literal(DISPUTE_REMEDIATION_KIND.REVERSE),
    targetTxId: z.string().uuid(),
    remediationTxId: z.string().uuid(),
    correctionId: z.string().uuid(),
    alreadyRemediated: z.boolean(),
  })
  .strict();

export type DisputeRemediationResponse = z.infer<typeof disputeRemediationResponseSchema>;

/** 403 khi actor tự khắc phục case do CHÍNH MÌNH mở (4-eyes trên dispute). */
export const SELF_RESOLVE_FORBIDDEN_CODE = "SELF_RESOLVE_FORBIDDEN" as const;
export const SELF_RESOLVE_FORBIDDEN_MESSAGE =
  "Người khắc phục phải khác người mở case (4-eyes).";

/* ───────────────────── (2) SECURITY CONSOLE — READ-ONLY ───────────────────── */

/** Tiền minor units dạng CHUỖI (B1) | null khi vai không được xem (redact fail-closed). */
const moneyMinorRedactable = z.union([z.string().regex(/^-?\d+$/), z.null()]);

/** 1 quỹ đang bị ĐÓNG BĂNG (F2 — `funds.status='frozen'`). KHÔNG tiền ⇒ không cần redact. */
export const frozenFundItemSchema = z
  .object({
    fundId: z.string().uuid(),
    fundName: z.string(),
    groupId: z.string().uuid(),
    currency: z.string().length(3),
    frozenAt: z.string().nullable(),
  })
  .strict();

export type FrozenFundItem = z.infer<typeof frozenFundItemSchema>;

/** 1 cảnh báo HÀNH VI (F2 `anomaly_alerts.kind='behavior'`). detail đã no-PII/no-tiền-thô (F2). */
export const consoleAnomalyItemSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    expenseId: z.string().uuid().nullable(),
    kind: z.literal(ANOMALY_ALERT_KIND_BEHAVIOR),
    detail: anomalyAlertDetailSchema,
    createdAt: z.string(),
  })
  .strict();

export type ConsoleAnomalyItem = z.infer<typeof consoleAnomalyItemSchema>;

/** 1 khoản chi đang trong CỬA SỔ NGUỘI (F2 `expenses.status='cooling'`). Tiền = string | null. */
export const coolingPendingItemSchema = z
  .object({
    expenseId: z.string().uuid(),
    fundId: z.string().uuid(),
    currency: z.string().length(3),
    amountMinor: moneyMinorRedactable, // 🔴 string BigInt | null — KHÔNG z.number()
    postAfter: z.string().nullable(),
  })
  .strict();

export type CoolingPendingItem = z.infer<typeof coolingPendingItemSchema>;

/** 1 dòng audit gần đây (con trỏ — KHÔNG PII, KHÔNG diff thô). */
export const consoleAuditItemSchema = z
  .object({
    id: z.string(),
    action: z.string(),
    entity: z.string(),
    entityId: z.string().nullable(),
    actorUserId: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();

export type ConsoleAuditItem = z.infer<typeof consoleAuditItemSchema>;

/**
 * Console READ (tổng hợp). `moneyVisible` phơi RÕ vai này có được xem tiền hay không (UI hiện "•••"
 * thay vì bịa số). KHÔNG có bất kỳ field mutation nào ở đây — read tách can-thiệp.
 */
export const securityConsoleReadSchema = z
  .object({
    moneyVisible: z.boolean(),
    frozenFunds: z.array(frozenFundItemSchema),
    anomalies: z.array(consoleAnomalyItemSchema),
    coolingPending: z.array(coolingPendingItemSchema),
    recentAudit: z.array(consoleAuditItemSchema),
  })
  .strict();

export type SecurityConsoleRead = z.infer<typeof securityConsoleReadSchema>;

/* ───────────────────── (3) CAN THIỆP — MUTATION (tách read) ───────────────────── */

export const SECURITY_INTERVENE_KIND = {
  FREEZE_FUND: "freeze_fund",
  CANCEL_COOLING: "cancel_cooling",
  FORCE_RESOLVE: "force_resolve",
} as const;
export type SecurityInterveneKind =
  (typeof SECURITY_INTERVENE_KIND)[keyof typeof SECURITY_INTERVENE_KIND];

export const securityInterveneKindSchema = z.enum([
  SECURITY_INTERVENE_KIND.FREEZE_FUND,
  SECURITY_INTERVENE_KIND.CANCEL_COOLING,
  SECURITY_INTERVENE_KIND.FORCE_RESOLVE,
]);

export const INTERVENE_REASON_MIN_LENGTH = 8;
export const INTERVENE_REASON_MAX_LENGTH = 2000;

/**
 * Body can thiệp. `reason` BẮT BUỘC (không có can-thiệp im lặng). `.strict()` + KHÔNG field tiền ⇒
 * endpoint này KHÔNG THỂ nhận số tiền để ghi thẳng vào sổ (mọi thay đổi tiền chỉ qua đường đảo).
 */
export const securityInterveneSchema = z
  .object({
    kind: securityInterveneKindSchema,
    targetId: z.string().uuid(),
    reason: z.string().min(INTERVENE_REASON_MIN_LENGTH).max(INTERVENE_REASON_MAX_LENGTH),
  })
  .strict();

export type SecurityInterveneBody = z.infer<typeof securityInterveneSchema>;

/**
 * Kết quả can thiệp — CON TRỎ + trạng thái mới. `ledgerTxId` chỉ khác null ở nhánh chạm tiền
 * (force_resolve ⇒ REVERSAL). freeze/cancel-cooling ⇒ LUÔN null (0 bút toán — bất biến ở contract).
 */
export const securityInterveneResponseSchema = z
  .object({
    kind: securityInterveneKindSchema,
    targetId: z.string().uuid(),
    newStatus: z.string(),
    ledgerTxId: z.string().uuid().nullable(),
  })
  .strict();

export type SecurityInterveneResponse = z.infer<typeof securityInterveneResponseSchema>;

/** Message biên (khớp toast admin). */
export const INTERVENE_TARGET_NOT_FOUND_MESSAGE = "Đối tượng can thiệp không tồn tại.";
export const FUND_ALREADY_FROZEN_MESSAGE = "Quỹ đã ở trạng thái đóng băng.";
